/**
 * El estado del plan, arriba de todo.
 *
 * Dos formas, según haga falta:
 * - **Aviso**: quedan pocos días de prueba, o ya terminó. Informa y no molesta: la prueba
 *   vencida **no bloquea nada**, porque el cargo lo hace Tienda Nube con la factura del
 *   plan de la tienda. Este cartel no pide apretar ningún botón, justamente porque no hay
 *   ningún botón que apretar.
 * - **Bloqueo**: Tienda Nube suspendió la app por falta de pago (o la Billing API informó
 *   un pago pendiente). Explica qué pasó, cuánto cuesta seguir, y **deja claro que
 *   deshacer y el historial siguen andando**. Esa última parte importa: alguien que ve un
 *   cartel de cobro asume que se quedó afuera de todo, y no es así.
 *
 * El texto del bloqueo lo escribe el backend, que es el mismo que devuelve el 402. Si la
 * pantalla lo reescribiera, el comerciante leería una cosa en el cartel y otra en el
 * error.
 */

import { Alert, Box, Text } from '@nimbus-ds/components';
import type { ReactElement } from 'react';
import type { Sesion } from '../api';

/** A partir de acá se avisa. Antes, la prueba recién empieza y el cartel sólo estorba. */
const DIAS_PARA_AVISAR = 5;

export function AvisoDePlan({ sesion }: { readonly sesion: Sesion | null }): ReactElement | null {
  if (sesion === null) return null;

  if (!sesion.acceso.puedeActualizar) {
    return (
      <Alert appearance="warning" title="No podés hacer actualizaciones nuevas">
        <Box display="flex" flexDirection="column" gap="1">
          <Text>{sesion.acceso.mensaje ?? ''}</Text>
          {/*
            Decía "sin límite de productos ni de actualizaciones" y desde que existe el
            techo de catálogo eso dejó de ser cierto: hay un tope técnico de variantes por
            corrida. El plan sí es sin límites —no cobramos por volumen ni por cantidad de
            corridas— y es lo único que este cartel puede afirmar.
          */}
          <Text fontSize="caption" color="neutral-textLow">
            El plan es de {sesion.plan.precioTexto}, sin cargo por cantidad de productos ni
            de actualizaciones.
          </Text>
        </Box>
      </Alert>
    );
  }

  // La prueba ya terminó. No hay nada que hacer: el plan sigue solo. Se avisa una vez y
  // sin alarma, porque un cartel de advertencia acá haría pensar que algo se rompió.
  if (sesion.suscripcion?.pruebaTermino === true) {
    return (
      <Alert appearance="primary" title="Tu prueba gratis terminó">
        <Text>
          Seguís usando la app con el plan de {sesion.plan.precioTexto}, que se cobra junto
          con tu factura de Tienda Nube. No tenés que hacer nada.
        </Text>
      </Alert>
    );
  }

  const restantes = sesion.suscripcion?.diasRestantes ?? null;
  if (restantes === null || restantes > DIAS_PARA_AVISAR) return null;

  return (
    <Alert appearance="primary" title={tituloDePrueba(restantes)}>
      <Text>
        Después seguís con el plan de {sesion.plan.precioTexto}, que se cobra junto con tu
        factura de Tienda Nube. No se corta nada.
      </Text>
    </Alert>
  );
}

function tituloDePrueba(dias: number): string {
  if (dias === 0) return 'Hoy se termina tu prueba gratis';
  if (dias === 1) return 'Te queda 1 día de prueba gratis';
  return `Te quedan ${String(dias)} días de prueba gratis`;
}
